import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const fmt = (n) => (n ? `+${n.slice(0, 2)} ${n.slice(2)}` : "");
const STATE = {
  ready: ["Recording ready", "bg-emerald-100 text-emerald-800"], missing: ["No recording yet", "bg-slate-100 text-slate-700"],
  outdated: ["Recording out of date", "bg-amber-100 text-amber-800"], failed: ["Last recording failed", "bg-rose-100 text-rose-800"],
  generating: ["Recording…", "bg-sky-100 text-sky-800"],
};

function Listen({ n }) {
  const [url, setUrl] = useState(null);
  useEffect(() => () => { if (url) { URL.revokeObjectURL(url); } }, [url]);
  if (!n.has_audio) { return null; }
  const load = async () => {
    try { setUrl(URL.createObjectURL((await api.get(`/receptionist/greetings/${n.number_id}/audio`, { responseType: "blob" })).data)); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return url ? <audio controls autoPlay src={url} className="h-9 w-64" data-testid={`gr-audio-${n.number_id}`} />
    : <Button variant="outline" size="sm" onClick={load} data-testid={`gr-listen-${n.number_id}`}>Listen to greeting</Button>;
}

function Editor({ n, voices, onSaved }) {
  const [f, setF] = useState({ greeting: n.pending?.greeting ?? n.greeting, greeting_lang: n.pending?.greeting_lang ?? n.greeting_lang, voice: n.pending?.voice ?? n.voice, name_say: n.pending?.name_say ?? n.name_say ?? "" });
  const [busy, setBusy] = useState(false);
  const [label, cls] = STATE[n.state] || [n.state, ""];
  const save = async () => {
    setBusy(true);
    try {
      const r = (await api.put(`/receptionist/greetings/${n.number_id}`, f)).data;
      toast.success(r.mode === "pending" ? "Saved. Recording your new greeting — callers keep hearing the current one until it is ready." : r.mode === "unchanged" ? "No changes" : "Greeting saved");
      onSaved(r.numbers);
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const sel = "mt-1 h-9 w-full rounded-md border px-2 text-sm dark:bg-slate-900";
  return (
    <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-800" data-testid={`gr-card-${n.number_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold">{fmt(n.number)}</div>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`} data-testid={`gr-state-${n.number_id}`}>{label}</span>
      </div>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300" data-testid={`gr-current-${n.number_id}`}>Callers hear: “{n.greeting}”</p>
      {n.pending?.state === "failed" && <p className="mt-1 text-xs text-rose-700" data-testid={`gr-failed-${n.number_id}`}>Your new greeting could not be recorded, so callers still hear the one above. Save again to retry.</p>}
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <label className="text-sm md:col-span-2">Greeting text
          <Input value={f.greeting} maxLength={300} onChange={(e) => setF({ ...f, greeting: e.target.value })} placeholder="Leave empty for the standard greeting" data-testid={`gr-text-${n.number_id}`} /></label>
        <label className="text-sm">Opening language
          <select value={f.greeting_lang} onChange={(e) => setF({ ...f, greeting_lang: e.target.value })} className={sel} data-testid={`gr-lang-${n.number_id}`}>
            <option value="en">English</option><option value="hi">Hindi (write the greeting in Hindi script)</option>
          </select></label>
        <label className="text-sm">Voice
          <select value={f.voice} onChange={(e) => setF({ ...f, voice: e.target.value })} className={sel} data-testid={`gr-voice-${n.number_id}`}>
            {voices.map((v) => <option key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</option>)}
          </select></label>
        <label className="text-sm md:col-span-2">How to say your business name (optional)
          <Input value={f.name_say} maxLength={60} onChange={(e) => setF({ ...f, name_say: e.target.value })} placeholder="e.g. Has-koo and Tails" data-testid={`gr-namesay-${n.number_id}`} /></label>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button disabled={busy || n.state === "generating"} onClick={save} data-testid={`gr-save-${n.number_id}`}>{busy ? "Saving…" : "Save greeting"}</Button>
        <Listen n={n} />
      </div>
    </div>
  );
}

export function ShallyGreetingCard() {
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.get("/receptionist/greetings").then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data?.numbers.some((n) => n.state === "generating" || n.pending?.state === "queued")) { return undefined; }
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [data, load]);
  if (!data?.numbers.length) { return null; }
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900" data-testid="shally-greeting-card">
      <h2 className="text-sm font-semibold">Shally's greeting</h2>
      <p className="mt-1 text-xs text-slate-500">What callers hear first. {data.auto ? "A new greeting is recorded and checked before callers hear it." : "Changes apply to the next call."} After the greeting, Shally continues in the caller's language.</p>
      <div className="mt-3 space-y-3">
        {data.numbers.map((n) => <Editor key={`${n.number_id}-${n.greeting}-${n.state}`} n={n} voices={data.voices} onSaved={(nums) => setData({ ...data, numbers: nums })} />)}
      </div>
    </section>
  );
}
